import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export type WahaLogLevel = 'warn' | 'info';

/**
 * Origens (repositórios) de onde a imagem do WAHA pode ser puxada.
 * Cada valor é o prefixo da imagem, já com o ':' final. A primeira é o padrão.
 */
export const WAHA_IMAGE_SOURCES: string[] = [
  'devlikeapro/waha-plus:',
  'nibocontainerregistry.azurecr.io/nibo/waha-fork:'
];

export const DEFAULT_WAHA_IMAGE_PREFIX = WAHA_IMAGE_SOURCES[0];

/**
 * Separa uma referência de imagem em repositório e tag.
 *
 * Não dá para usar um simples split(':'): o registry pode ter porta
 * (`host:5000/repo:tag`), então o ':' só separa a tag quando vem depois da última '/'.
 * Referência por digest (`repo@sha256:...`) não tem tag — devolve o repositório e tag vazia,
 * porque a tela trabalha com "repositório + tag" e não sabe representar um digest.
 */
export function splitWahaImageReference(image: string | undefined): { repository: string; tag: string } {
  const value = String(image ?? '').trim();
  if (!value) {
    return { repository: '', tag: '' };
  }

  const digestIndex = value.indexOf('@');
  if (digestIndex > 0) {
    return { repository: value.substring(0, digestIndex), tag: '' };
  }

  const colonIndex = value.lastIndexOf(':');
  if (colonIndex > value.lastIndexOf('/')) {
    return { repository: value.substring(0, colonIndex), tag: value.substring(colonIndex + 1).trim() };
  }

  return { repository: value, tag: '' };
}

/** Tag de uma imagem do WAHA já existente (vinda do pod ou dos defaults). */
export function extractWahaImageTag(image: string | undefined): string {
  return splitWahaImageReference(image).tag;
}

/**
 * Descobre a origem de uma imagem do WAHA já existente.
 *
 * Origem fora da lista conhecida devolve o próprio repositório da imagem, nunca o padrão:
 * cair no padrão faria a tela exibir `devlikeapro/waha-plus:` para uma conexão que roda outro
 * repositório e, num "Atualizar conexão" feito por qualquer outro motivo, trocaria a imagem dela
 * em silêncio. Só imagem ausente ou vazia usa o padrão.
 */
export function resolveWahaImagePrefix(image: string | undefined): string {
  const { repository } = splitWahaImageReference(image);
  if (!repository) {
    return DEFAULT_WAHA_IMAGE_PREFIX;
  }

  const prefix = `${repository}:`;
  return WAHA_IMAGE_SOURCES.find(source => source.toLowerCase() === prefix.toLowerCase()) ?? prefix;
}

/**
 * Opções a exibir no select para uma imagem já configurada: as origens conhecidas e,
 * quando a imagem vem de um repositório fora da lista, a origem dela também — senão
 * não haveria como a tela mostrar (nem preservar) o que a conexão realmente usa.
 */
export function wahaImageSourcesIncluding(prefix: string | undefined): string[] {
  const value = String(prefix ?? '').trim();
  if (!value || WAHA_IMAGE_SOURCES.some(source => source.toLowerCase() === value.toLowerCase())) {
    return [...WAHA_IMAGE_SOURCES];
  }

  return [...WAHA_IMAGE_SOURCES, value];
}

/**
 * Tamanhos de disco de sessão (PVC) permitidos, espelhando VolumeSizeRule na API.
 * Configuração exclusivamente por conexão — nunca global/default.
 */
export type WhatsAppVolumeSize = '4Gi' | '8Gi' | '16Gi';

/** Valores permitidos em ordem crescente de tamanho — a posição no array é o "rank". */
export const WHATSAPP_VOLUME_SIZES: WhatsAppVolumeSize[] = ['4Gi', '8Gi', '16Gi'];

/**
 * Posição ordinal do tamanho (0 = 4Gi, 1 = 8Gi, 2 = 16Gi). Valores inválidos caem para 4Gi.
 * O Kubernetes só permite expandir um PVC, nunca reduzir, então esse rank é usado para
 * bloquear reduções tanto no formulário quanto na validação de envio.
 */
export function whatsAppVolumeSizeRank(volumeSize: string | undefined): number {
  const index = WHATSAPP_VOLUME_SIZES.indexOf(String(volumeSize ?? '').trim() as WhatsAppVolumeSize);
  return index >= 0 ? index : 0;
}

type WhatsAppUpdateSettingsBasePayload = {
  wahaImage: string;
  wahaLogLevel: WahaLogLevel;
  wahaEnableTagEvents: boolean;
  niboWahaImage: string;
  niboWahaChartVersion: string;
  vpaConfigWAHAMinAllowedMemory: string;
  vpaConfigWAHAMaxAllowedMemory: string;
};

export type WhatsAppUpdateAllConnectionsSettingsPayload = WhatsAppUpdateSettingsBasePayload;

export type WhatsAppUpdateConnectionSettingsPayload = WhatsAppUpdateSettingsBasePayload & {
  connectionId: string;
  // VolumeSize é exclusivo da atualização por conexão (não existe no payload de "todas as conexões").
  volumeSize: WhatsAppVolumeSize;
};

export type WhatsAppNewConnectionsDefaultsPayload = {
  wahaImage: string;
  niboWAHAImage: string;
  niboWAHAChartVersion: string;
  vpaConfigWAHAMinAllowedMemory: string;
  vpaConfigWAHAMaxAllowedMemory: string;
};

@Injectable({
  providedIn: 'root'
})
export class WhatsappSettingsService {
  public readonly niboWahaImagePrefix: string;

  constructor(private readonly httpClient: HttpClient) {
    this.niboWahaImagePrefix = this.getNiboWahaImagePrefix();
  }

  private isTestServerEnvironment(): boolean {
    const api = String(environment.whatsAppApi ?? '').toLowerCase();
    return !environment.production || api.includes('test-') || api.includes('localhost');
  }

  public getNiboWahaImagePrefix(): string {
    return this.isTestServerEnvironment()
      ? 'nibocontainerregistry.azurecr.io/nibo/nibo-waha-test:'
      : 'nibocontainerregistry.azurecr.io/nibo/nibo-waha:';
  }

  public updateAllConnections(payload: WhatsAppUpdateAllConnectionsSettingsPayload) {
    return this.httpClient.put<void>(
      `${environment.whatsAppApi}/infrastructure/connections/updateversion`,
      payload
    );
  }

  public updateConnection(payload: WhatsAppUpdateConnectionSettingsPayload) {
    return this.httpClient.put<void>(
      `${environment.whatsAppApi}/infrastructure/connections/${payload.connectionId}/updateversion`,
      payload
    );
  }

  public getObservabilityScreenshot(connectionId: string) {
    return this.httpClient.get(
      `${environment.whatsAppApi}/connections/${connectionId}/observability/ss`,
      { responseType: 'blob' }
    );
  }

  public getNewConnectionsDefaults() {
    return this.httpClient.get<WhatsAppNewConnectionsDefaultsPayload>(
      `${environment.whatsAppApi}/infrastructure/connections/defaults`
    );
  }

  public setNewConnectionsDefaults(payload: WhatsAppNewConnectionsDefaultsPayload) {
    return this.httpClient.put<void>(
      `${environment.whatsAppApi}/infrastructure/connections/defaults`,
      payload
    );
  }
}
